import React from 'react';
import { clearAuthData } from '../utils/clearAuth';

// Global error boundary. Any uncaught React render error anywhere in the app is
// surfaced as a dismissible popup/modal (NOT a full-page "Something went wrong"
// takeover). Dismissing resets the boundary so the app re-renders and the user
// can keep working. Async errors are handled separately via try/catch + toasts.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleDismiss = () => {
    // Reset so children re-render (retry). If the error is persistent it will
    // re-throw and show the modal again — still recoverable, never a dead page.
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleClearAuth = () => {
    clearAuthData();
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    return (
      <>
        {/* While errored, unmount children to avoid re-throwing during this render. */}
        {!hasError && this.props.children}

        {hasError && (
          <div
            className="modal show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2050 }}
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) this.handleDismiss(); }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header border-0">
                  <h5 className="modal-title d-flex align-items-center gap-2 text-danger">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    Unexpected error
                  </h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={this.handleDismiss}></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted mb-1">
                    Something went wrong. Close this message and try again, or reload the page if the problem continues.
                  </p>
                  {process.env.NODE_ENV === 'development' && error && (
                    <details className="mt-2 text-start">
                      <summary className="small text-muted">Error details (development)</summary>
                      <pre className="text-danger small mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                        {error && error.toString()}
                        {errorInfo && errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
                <div className="modal-footer border-0">
                  <button type="button" className="btn btn-outline-secondary" onClick={this.handleDismiss}>
                    Close
                  </button>
                  <button type="button" className="btn btn-outline-warning" onClick={this.handleClearAuth}>
                    Clear session
                  </button>
                  <button type="button" className="btn btn-primary" onClick={this.handleReload}>
                    Reload
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

export default ErrorBoundary;
